import { useEffect, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Loader } from '@mantine/core'

import DetailSection from 'src/components/DataDisplay/DetailSection/DetailSection'
import { GET_CARRIER_CONTACT_HISTORY } from 'src/graphql/carriers.graphql'

import type { ContactHistoryEntry } from '../carrier.types'
import { contactLabel } from '../carrier.utils'

import CarrierContactHistory from './CarrierContactHistory'

export const HISTORY_PER_PAGE = 10

export interface CarrierContactHistoryPanelProps {
  carrierId: string
  /** Narrows to one contact. Cleared from inside the panel. */
  contactId?: string | null
  onClearFilter?: () => void
  /** Bumped by the caller after a change, to re-read the trail. */
  reloadKey?: number
}

/**
 * Fetches a page of a carrier's contact audit trail and hands it to the table.
 */
const CarrierContactHistoryPanel = ({
  carrierId,
  contactId,
  onClearFilter,
  reloadKey = 0,
}: CarrierContactHistoryPanelProps) => {
  const [page, setPage] = useState({ offset: 0, limit: HISTORY_PER_PAGE })

  const { data, loading, error, refetch } = useQuery(
    GET_CARRIER_CONTACT_HISTORY,
    {
      variables: {
        carrierId,
        filters: { contactId: contactId ?? null },
        pageInfo: page,
      },
      fetchPolicy: 'cache-and-network',
    }
  )

  // Narrowing to one contact shortens the trail, so the page it was sitting on
  // may not exist any more.
  useEffect(() => {
    setPage((current) => ({ ...current, offset: 0 }))
  }, [contactId])

  // A delete or an edit next door writes entries this panel is showing, and
  // nothing in its own variables changed — so it is told to read again.
  useEffect(() => {
    if (reloadKey > 0) refetch()
  }, [reloadKey, refetch])

  const entries: ContactHistoryEntry[] =
    data?.carrierContactHistory?.items ?? []
  const totalCount = data?.carrierContactHistory?.totalCount ?? 0

  // Named from the entries themselves: the filter is an id, and the contacts
  // table next door may not be showing the row it belongs to.
  const filteredContact = contactId
    ? entries.find((entry) => entry.contact.contactId === contactId)?.contact
    : null

  // Shown while a filtered read is in flight too. Skipping it there meant
  // "nothing recorded against this contact" was on screen before the answer
  // was known, which is a statement rather than a wait.
  if (loading && entries.length === 0) {
    return (
      <DetailSection title="Contact History" columns={1}>
        <div className="flex items-center gap-2 py-2 text-sm text-doubleNickel-gray-500">
          <Loader size="sm" />
          Loading history
        </div>
      </DetailSection>
    )
  }

  // "Nothing was recorded" and "we could not read it" are different answers,
  // and only one of them is about this carrier.
  if (error && entries.length === 0) {
    return (
      <DetailSection title="Contact History" columns={1}>
        <div className="rounded-lg border border-doubleNickel-error-200 bg-doubleNickel-error-25 p-3 text-sm text-doubleNickel-error-700">
          The history could not be loaded, so this is not a statement about what
          happened to these contacts. {error.message}
        </div>
      </DetailSection>
    )
  }

  return (
    <CarrierContactHistory
      entries={entries}
      totalCount={totalCount}
      pageSize={page.limit}
      // Narrowing to one contact resets the offset out here; DnTable counts
      // its own pages, so it has to be told.
      currentPage={Math.floor(page.offset / page.limit) + 1}
      onLoadPage={setPage}
      filteredContactLabel={
        contactId
          ? filteredContact
            ? contactLabel(filteredContact)
            : 'one contact'
          : null
      }
      onClearFilter={onClearFilter}
    />
  )
}

export default CarrierContactHistoryPanel
